import type { Styles } from '@adornis/ass/style.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Einzelberatung, EinzelberatungSlot } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { ComponentSizeController } from '@adornis/chemistry/controllers/component-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { useContext } from '@adornis/functional-lit/directive-provide-context.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { createContext, provide } from '@lit/context';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import {
  chooseValueByLang,
  getProductTags,
  renderDateTimeBoxPair,
  renderProductCardTags,
  type ProductCardData,
} from './productCardShared.js';

export type CounselingCardData = ProductCardData & {
  counseling: NarrowedEntity<
    Einzelberatung,
    {
      _id: 1;
      isLDAPOnly: 1;
      timeframe: { name: 1 };
      hasPrice: 1;
      prices: { unitAmount: 1 };
      presenceType: 1;
      language: 1;
      topic: 1;
      name: 1;
      translatedDescriptionHTML: { langKey: 1; text: 1 };
      start: 1;
      end: 1;
      slotConfig: { start: 1; end: 1; slotDuration: 1; hasLunchBreak: 1; startLunch: 1; endLunch: 1 };
      productEntryRequirementLabels: 1;
    }
  >;
  slotData: Array<{
    timeslot: EinzelberatungSlot;
    isSelected: boolean;
    hasTimeCollision: boolean;
  }>;
  bookedAlready: boolean;
  events?: {
    onSlotSelect?: (slot: EinzelberatungSlot) => void;
  };
};

export const counselingCardDataContext = createContext<CounselingCardData>('counseling-card-data');
const IsMobileViewEnabledContext = createContext<boolean>('is-mobile-view-enabled');

@customElement('bh-counseling-card')
export class BHCounselingCard extends ChemistryLitElement {
  @state() private readonly size = new ComponentSizeController(this);

  @provide({ context: IsMobileViewEnabledContext })
  @state()
  isMobileViewEnabled = true;

  override render() {
    this.isMobileViewEnabled = (this.size.width ?? 0) < 768;
    return html` <slot></slot> `;
  }

  override styles(): Styles[] {
    const shadow = '0 1px 3px 0 #d4d4d5, 0 0 0 1px #d4d4d5';
    const raisedShadow = '0 0 0 1px #d4d4d5, 0 2px 4px 0 rgba(34,36,38,.12), 0 2px 10px 0 rgba(34,36,38,.15)';
    const hoverShadow = '0 1px 3px 0 #bcbdbd, 0 0 0 1px #d4d4d5';
    const hoverStyle = { transform: 'translateY(-2px)', boxShadow: hoverShadow };

    return [
      ...super.styles(),
      {
        ':host': {
          display: 'flex',
          borderRadius: this.sizes.borderRadius,
          boxShadow: shadow,
          overflow: 'hidden',
          boxSizing: 'border-box',
          background: this.colors.white,
          position: 'relative',
          padding: '20px',
          userSelect: 'none',
          '&[raised]': { boxShadow: raisedShadow },
          '&:hover': { '&[href]': hoverStyle },
          '@media print': { boxShadow: 'none', border: `1px solid ${this.colors.neutral.shade(200)}` },
        },
      },
    ];
  }
}

const counselingCardDescription = stateful<CounselingCardData>(({ counseling }) => {
  return html`
    <bh-expandable-content
      @click=${event => {
        if (event.target.tagName === 'TR-LINK') {
          // do not select card when user clicked on Link
          event.stopPropagation();
        }
      }}
    >
      <tr-flex ${css({ gap: '1em' })}>
        ${chooseValueByLang(
          // TODO: fix types
          TextMultipleLangItem.mapAllTranslationsByLang(counseling.translatedDescriptionHTML as any),
        )}
      </tr-flex>
    </bh-expandable-content>
  `;
});

const counselingCardSlots = stateful<CounselingCardData>(({ slotData, events, bookedAlready, colorDef }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);
  const translate = clientTranslator.translate.bind(clientTranslator);

  if (!theme) throw new Error('theme is undefined');

  return html`
    <x-flex horizontal space="sm" crossaxis-start wrap>
      ${slotData.map(({ timeslot, isSelected, hasTimeCollision }) => {
        return html`
          <tr-flex
            horizontal
            crossaxis-end
            space="sm"
            ${css({
              padding: theme.spacing.sm,
              borderRadius: theme.sizes.borderRadius,
              border: isSelected ? `1px solid ${colorDef.stepColor}` : `1px solid ${theme.colors.tone.black}`,
              cursor: hasTimeCollision ? 'not-allowed' : 'pointer',
              position: 'relative',
              opacity: bookedAlready || hasTimeCollision || timeslot.isBooked ? '0.5' : '1',
              pointerEvents: bookedAlready || hasTimeCollision || timeslot.isBooked ? 'none' : 'all',
            })}
            @click=${() => {
              if (!bookedAlready && !hasTimeCollision) events?.onSlotSelect?.(timeslot);
            }}
          >
            <div
              ${css({
                display: isSelected ? 'block' : 'none',
                position: 'absolute',
                width: '100%',
                height: '100%',
                inset: '0',
                opacity: '.12',
                background: colorDef.stepColor,
              })}
            ></div>
            <tr-flex space="sm">
              <tr-text>von ${timeslot.start!.toLocaleString(DateTime.TIME_24_SIMPLE)}</tr-text>
              <tr-text>bis ${timeslot.end!.toLocaleString(DateTime.TIME_24_SIMPLE)}</tr-text>
            </tr-flex>
            ${bookedAlready
              ? html` <tr-tooltip .text=${translate('REGISTRATION', 'appointmentAlreadyBookedTooltip')}> </tr-tooltip> `
              : hasTimeCollision
              ? html`
                  <tr-tooltip .text=${translate('REGISTRATION', 'cannotSelectAppointmentCollisionTooltip')}>
                  </tr-tooltip>
                `
              : nothing}
            ${timeslot.isBooked
              ? html`
                  <tr-flex space="sm">
                    <tr-icon
                      ${css({
                        background: theme.colors.tone.warning,
                        borderRadius: theme.sizes.borderRadius,
                        padding: theme.spacing.xxs,
                        color: theme.colors.tone.black,
                        display: 'none', // disable this for now (maybe needed later (when waitinglist should also work for einzelberatungen))
                      })}
                      small
                    >
                      priority_high
                    </tr-icon>
                    <tr-tooltip
                      .text=${html`
                        ${translate('REGISTRATION', 'seat_not_available')}. <br />
                        ${translate('REGISTRATION', 'queue_possible')}
                      `}
                    >
                    </tr-tooltip>
                  </tr-flex>
                `
              : nothing}
            ${isSelected
              ? html`
                  <tr-icon
                    ${css({
                      position: 'absolute',
                      right: '-10px',
                      top: '-10px',
                      background: colorDef.stepColor,
                      borderColor: colorDef.stepColor,
                      borderRadius: theme.sizes.borderRadius,
                      padding: theme.spacing.xxs,
                      color: '#fff',
                    })}
                    small
                  >
                    task_alt
                  </tr-icon>
                `
              : nothing}
          </tr-flex>
        `;
      })}
    </x-flex>
  `;
});

export const counselingCardTags = stateful<CounselingCardData>(({ counseling }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);
  const translate = clientTranslator.translate.bind(clientTranslator);

  const productLabels = useObservable(() => getProductTags(counseling), [counseling._id]);

  if (!theme) throw new Error('theme is undefined');
  if (!productLabels) return nothing;

  const tags = [
    ...productLabels,
    // {
    //   icon: 'home',
    //   label: (counseling.slotConfig?.slotDuration + ' ' + translate('minutes')) as string,
    // },
  ];

  return renderProductCardTags(tags);
});

const counselingCardTerminButton = stateful<CounselingCardData>(({ counseling, colorDef, start, end }) => {
  const showMobileView = useContext(IsMobileViewEnabledContext);
  if (!counseling.slotConfig) return nothing as Renderable;

  return html`
    <tr-grid
      space="xxs"
      ${css({
        alignItems: 'stretch',
        gridTemplateColumns: showMobileView ? '1fr 1fr' : '1fr min-content',
      })}
    >
      ${(() => {
        const date = counseling.slotConfig.start;
        const startTime = counseling.slotConfig.start.toLocaleString(DateTime.TIME_24_SIMPLE);
        const endTime = counseling.slotConfig.end.toLocaleString(DateTime.TIME_24_SIMPLE);
        const timeStackStyle = showMobileView ? 'horizontal' : 'vertical';

        return renderDateTimeBoxPair({ date, startTime, endTime, colorDef, timeStackStyle });
      })()}
    </tr-grid>
  `;
});

export const counselingCardParts = {
  counselingCardDescription,
  counselingCardSlots,
  counselingCardTags,
  counselingCardTerminButton,
};
